import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { delay, switchMap, tap } from 'rxjs/operators';
import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, ApplicationStateChangeRequest, WorkflowData, WorkflowNote } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { NoteType } from '../../../common/models/note-type.enum';
import { SidePanel } from '../../../common/models/side-panel.model';
import { Well } from '../../../common/models/well.model';
import { DateService } from '../../../common/services/date.service';
import { IUploadFile } from '../../../common/services/document-management.service';
import { IssuesService } from '../../../common/services/issues.service';
import { LoaderService } from '../../../common/services/loader.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { WellService } from '../../../common/services/well.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { AuthService } from '../../../services/auth.service';
import { OnBaseDocumentTypeProcessReference} from '../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-surface-hole-additive-review',
    templateUrl: './surface-hole-additive-review.component.html',
    styleUrls: ['./surface-hole-additive-review.component.scss'],
    standalone: false
})
export class SurfaceHoleAdditiveReviewComponent extends ApplicationBaseComponent implements OnInit {

    applicationState: ApplicationState;
    storedFiles: IUploadFile[] = [];
    appStatus: string = "";
    totalDepth: Date | null;

    isInReview: boolean = false;
    @Input() app: WorkflowData;
    @Input() well: Well;

    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    get isLoading(): boolean {
        return this.loading$.value;
    }

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingObs$: Observable<boolean> = this.loading$.asObservable();


    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

    get receivedDate(): Date {
        if (this.form == null) return null;
        let ctrl = this.form.get('receivedDate');
        if (ctrl == null) return null;
        let value: Date = (ctrl.value != null && ctrl.value != '') ? new Date(ctrl.value) : null;
        return value;
    }

    get isApproved(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Approved;
    }

    get isInCorrections(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Corrections;
    }


    get filesUploaded(): boolean {
       
        if (this.application.workflowFieldDatas.find(f => f.formControlName == "uploadedFiles").workflowFieldFiles.length > 0) {
            return true
        }
        return false;
    }

    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });


    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);


    returnedDialog: Dialog = new Dialog("Return this application for corrections?", [new DialogButton("Confirm", true, () => { this.onReturnConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    canceledDialog: Dialog = new Dialog("Leave review unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.home(); }), new DialogButton("No", true, () => { }),]);
    returnedConfirmedDialog: Dialog = new Dialog("The application has been returned for corrections.", [new DialogButton("OK", true, () => { this.home(); })]);
    savedDialog: Dialog = new Dialog("Save the review and return to the homepage?", [new DialogButton("Yes", true, () => { this.onSaveConfirmed(); }), new DialogButton("No", true, () => { }),]);
    saveConfirmedDialog: Dialog = new Dialog("The review has been saved.", [new DialogButton("OK", true, () => { this.home(); })]);


    constructor(private route: ActivatedRoute, private builder: FormBuilder, public loader: LoaderService, public dialogService: DynamicDialogService, public _subheader: SubHeaderService, _auth: AuthService,
        public appService: ApplicationsService, _sidePanelService: SidePanelService, _notesService: ApplicationNotesService, _router: Router, public domainService: DomainDefinitionService, public issuesService: IssuesService, public wellService: WellService, public dateService: DateService) {
        super(loader, dialogService, _subheader, _auth, appService, _router, _sidePanelService, _notesService);
        this.dateFields = ['receivedDate']
        this.fileFields = ['uploadedFiles']
		this.trueBooleanFields = ['hasNoAdditives'];
    }

    ngOnInit() {

        this.form = this.buildForm();
        this.isInReview = true;
        this.application = this.app;




        this.form.get("hasNoAdditives").disable();
      

        for (let i = 0; i < this.application.workflowFieldDatas.find(f => f.formControlName == 'uploadedFiles').workflowFieldFiles.length; i++) {

            this.storedFiles.push(this.convertWorkflowFieldFileToIUploadFile(this.application.workflowFieldDatas.find(f => f.formControlName == 'uploadedFiles').workflowFieldFiles[i]))
        }
        this.appStatus = this.app.applicationState
        this.applicationState = this.app.applicationState;
        if (this.application.applicationState == ApplicationState.Approved) {
            this.form.disable();
        }

        if (this.isInternal && this.application.applicationState == ApplicationState.Corrections) {
            this.form.disable();
        }

        this.totalDepth = this.well.dateTotalDepthReached;

        if (this.application != null) {


			this.form = this.restoreFormData(this.form);


            this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
            if (this.application.applicationState != ApplicationState.Approved && this.isInternal) {
                this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
                this.form.get("receivedDate").setValue(this.application.workflowFieldDatas.find(f => f.formControlName == 'receivedDate').dateData);

            }
            this.subheaderService.updateSubHeader(this.subheader);

        }
   

    }

    ngOnDestroy() {
        this.isInReview = false;
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }


    buildForm(): FormGroup {

        return this.builder.group({
            wellName: ["", []],
            wellNumber: [null, []],
            receivedDate: ["", []],
            hasNoAdditives: [false, []],

        });
    }

    onSave(): void {

        this.savedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.savedDialog);

    }
    onSaveConfirmed(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress();
       
        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.loader.toggleLoader(false);
                    this.saveConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.saveConfirmedDialog
                    );

                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);

    }



    onAccept(): void {

        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed() {


        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {

                let destination: ApplicationState = ApplicationState.Approved;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.loader.toggleLoader(false);
                    this.acceptConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.acceptConfirmedDialog);

                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }

    onReturn() {
        this.returnedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.returnedDialog);

    }

    onReturnConfirmed() {

        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Corrections;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.loader.toggleLoader(false);
                    this.returnedConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.returnedConfirmedDialog);


                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$)

    }

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }

    onDateUpdated(date: Date, field: string): void {
        let ctrl = this.form.get(field);
        if (ctrl != null) ctrl.setValue(date);
    }

    cancel() {

        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }


}
